/**
 * Screen 2: what this file is about to do, before it does any of it.
 *
 * The preview is not a feature bolted onto the import - it is the Plan, shown.
 * Every row already carries a verdict and the reason for it, so this screen
 * only has to group them so a person can see the shape of what is coming, and
 * argue with the parts they disagree with.
 *
 * Two things it must get right, both of them learned the hard way:
 *
 * A file with two hundred repeats cannot be answered a row at a time. Not
 * because it is tedious - because what actually happens is somebody answers the
 * first few and the rest take the default without anyone deciding. So the
 * answer is offered for the class, and per-row is the exception on top of it.
 *
 * And a row that is going in can still deserve a warning. A renamed row is
 * added beside the record it came from rather than into it, and if nothing said
 * so the person would find out by ending up with two.
 *
 * Groups are keyed on the verdict the plan arrived with, never the amended one,
 * so a group does not disappear from under the person who just answered it.
 */
import { Component, EventEmitter, Input, Output } from '@angular/core';

import {
  UnwritableColumn,
  PlanAction,
  PlanRow,
  TransferPlan,
  TransferSchema,
} from '../../services/table-import-export.service';
import { ReasonCopy, notesToShow, reasonCopy } from './reason-copy';
import {
  ReasonGroup,
  RowDecisions,
  classAnswer,
  countActions,
  effectiveAction,
  groupByReason,
} from './plan-decisions';

/** One reason, with the wording and the state its control is in. */
export interface PlanReasonGroup extends ReasonGroup {
  copy: ReasonCopy;
  /** Null when nobody has answered for the class. */
  answer: PlanAction | null;
  /** The ones actually drawn. See RowsShown. */
  shown: PlanRow[];
  /** How many of this group are not drawn. */
  hidden: number;
}

/**
 * HOW MANY ROWS OF A CLASS ARE DRAWN.
 *
 * A group is a class of rows answered as a class, so what the list is FOR is
 * recognising the class - checking that "no address" really is what these are
 * - and forty thousand of them do that no better than two hundred. What forty
 * thousand do instead is put forty thousand rows in the DOM: the same freeze
 * the settled-fields note below is about, reached from the other direction,
 * and this time by the size of somebody's file rather than by a getter.
 *
 * Nothing is answered less: the class control acts on the REASON, so it
 * covers every row of the group whether or not it was drawn. The per-row
 * exception is what a row past the cap loses, and a list nobody can scroll
 * was not offering that either.
 */
const RowsShown = 200;

@Component({
  selector: 'app-import-plan-step',
  templateUrl: './import-plan-step.component.html',
  styleUrls: ['./import-plan-step.component.css'],
})
export class ImportPlanStepComponent {
  /**
   * Setters rather than ngOnChanges, because the view below is a function of
   * these two and nothing else. ngOnChanges only fires for a template binding,
   * so a component assigned to directly - which is how it is driven under
   * test, and how somebody will eventually drive it in anger - would have kept
   * the previous plan on screen. What the view is made of should not depend on
   * how the input arrived.
   */
  @Input({ required: true })
  set plan(value: TransferPlan) { this.planValue = value; this.settle(); }
  get plan(): TransferPlan { return this.planValue; }
  private planValue!: TransferPlan;

  @Input({ required: true }) schema!: TransferSchema;

  @Input()
  set decisions(value: RowDecisions) { this.decisionValues = value; this.settle(); }
  get decisions(): RowDecisions { return this.decisionValues; }
  private decisionValues: RowDecisions = { rowActions: {}, reasonActions: {} };
  @Output() decisionsChange = new EventEmitter<RowDecisions>();

  private readonly opened = new Set<string>();

  /** Recomputed from the answers, so the headline moves as they are given. */
  get counts(): Record<PlanAction, number> {
    return countActions(this.plan.rows, this.decisions);
  }

  get total(): number { return this.plan.rows.length; }

  /**
   * WHAT THE SCREEN IS SHOWING, settled once per answer rather than per frame.
   *
   * These were getters, and *ngFor read them on every change detection pass.
   * Grouping builds new group objects each time it runs, and *ngFor compares
   * the ITEMS it is handed rather than the array around them - so brand-new
   * groups read as "every row here has been replaced", and the subtree was
   * torn down and rebuilt on every pass for as long as the screen was open.
   * On a real file that is a hundred-odd rows rebuilt per frame, and the tab
   * stopped answering: the buttons would not take a click, and the words were
   * still those of the previous step because the frame that would have drawn
   * this one never finished. Every spec used four-row plans, comfortably under
   * the size where it shows.
   *
   * Every iterable in the template is settled here, including the notes.
   *
   * The notes are settled for cost, not for correctness, and the difference
   * matters to whoever reads this next. ngFor's differ compares the ITEMS of
   * the iterable, not the iterable itself: a fresh array of the same strings
   * produces no diff and rebuilds nothing. Binding `notesOn(row)` directly
   * was measured both ways - it is green with the method and green without
   * it - so it was never what froze the screen. What it did cost was one
   * array per row per check, 125 of them on a real file, and settling them
   * beside the row they belong to removes that for the price of one field.
   *
   * What DOES rebuild is a new OBJECT, which a differ cannot recognise
   * across a check. Grouping makes them, which is why the groups below are
   * fields and not getters, and why the same change had to be made on the
   * mapping and result screens.
   *
   * trackBy does NOT help here. It decides which DOM nodes survive a
   * rebuild; the loop is upstream of that, in the decision that a rebuild
   * is needed at all. Anybody reaching for trackBy is about to conclude
   * the problem is elsewhere.
   *
   * They still have to move - answering for a class is what this step is for -
   * so they are recomputed whenever the plan or the answers change, and never
   * merely because something asked to look at them.
   */
  heldBack: PlanReasonGroup[] = [];
  refused: PlanReasonGroup[] = [];
  /** Each row with its notes already worked out, so the template iterates a
   *  list it was given rather than one it asks for. Capped like the groups
   *  below, and unlike them it is drawn without being asked for. */
  worthALook: { row: PlanRow; notes: string[] }[] = [];
  /** How many more there are than are drawn, and how many there are in all -
   *  the count in the heading is about the file, not about the list. */
  worthALookHidden = 0;
  worthALookTotal = 0;

  /**
   * Said once, above the rows, because it is true of a COLUMN and not of a
   * row. It used to be written into every row's sentence, so a four-row file
   * carried the same paragraph four times and a real one carried it in the
   * hundreds - which teaches people to stop reading row text, including the
   * part that is about their row.
   */
  unwritable: UnwritableColumn[] = [];

  private settle(): void {
    if (!this.plan) return;
    // Only the ones the file actually put something in. Every unwritable
    // column now arrives here, including the empty ones, because the mapping
    // screen needs to know they are OURS rather than columns we have never
    // heard of. This notice does not: telling somebody their export came back
    // with an untouched Score column is a true line nobody needs, and those
    // are how people learn to stop reading notices.
    this.unwritable = (this.plan.mapping?.unwritable ?? [])
      .filter(column => (column.rows ?? 0) > 0);
    this.heldBack = this.describe(groupByReason(this.rowsPlannedAs('Skip')));
    this.refused = this.describe(groupByReason(this.rowsPlannedAs('Blocked')));
    const worthALook = this.plan.rows
      .filter(row => (row.notes?.length ?? 0) > 0 && this.willBeWritten(row));
    this.worthALookTotal = worthALook.length;
    this.worthALookHidden = Math.max(0, worthALook.length - RowsShown);
    this.worthALook = worthALook
      .slice(0, RowsShown)
      .map(row => ({ row, notes: notesToShow(row.notes ?? []) }));
  }

  noteCopy(code: string): ReasonCopy { return reasonCopy(code); }

  willBeWritten(row: PlanRow): boolean {
    const action = effectiveAction(row, this.decisions);
    return action === 'Create' || action === 'Update';
  }

  actionOf(row: PlanRow): PlanAction {
    return effectiveAction(row, this.decisions);
  }

  isOpen(reason: string): boolean { return this.opened.has(reason); }

  toggleOpen(reason: string): void {
    if (!this.opened.delete(reason)) this.opened.add(reason);
  }

  /** One answer for every row held back for this reason. */
  answerClass(reason: string, action: PlanAction | null): void {
    const reasonActions = { ...this.decisions.reasonActions };
    if (action === null) delete reasonActions[reason];
    else reasonActions[reason] = action;
    this.emit({ ...this.decisions, reasonActions });
  }

  /**
   * The exception on top of the class answer. Clearing it puts the row back
   * under whatever the class says, which is what "except this one, actually
   * no" has to mean.
   */
  answerRow(rowNo: number, action: PlanAction | null): void {
    const rowActions = { ...this.decisions.rowActions };
    if (action === null) delete rowActions[rowNo];
    else rowActions[rowNo] = action;
    this.emit({ ...this.decisions, rowActions });
  }

  hasOwnAnswer(rowNo: number): boolean {
    return this.decisions.rowActions[rowNo] !== undefined;
  }

  /**
   * Enough of a row to recognise it, taken in the entity's own field order.
   * The schema decides what identifies a record here, which is why this screen
   * never has to know whose rows it is showing.
   */
  identify(row: PlanRow): string {
    return this.schema.fields
      .map(field => row.values[field.name])
      .filter((value): value is string => !!value && value.trim() !== '')
      .slice(0, 2)
      .join(' — ');
  }

  private rowsPlannedAs(action: PlanAction): PlanRow[] {
    return this.plan.rows.filter(row => row.action === action);
  }

  private describe(groups: ReasonGroup[]): PlanReasonGroup[] {
    return groups.map(group => ({
      ...group,
      copy: reasonCopy(group.reason),
      answer: classAnswer(group.reason, this.decisions),
      // Settled here with everything else the template iterates, for the
      // reason the note on the fields above gives.
      shown: group.rows.slice(0, RowsShown),
      hidden: Math.max(0, group.rows.length - RowsShown),
    }));
  }

  private emit(decisions: RowDecisions): void {
    // Through the setter, which settles the view: the answers are half of
    // what it is made of.
    this.decisions = decisions;
    this.decisionsChange.emit(decisions);
  }
}
